// The one modal the staff ingress check surfaces (#10718).
//
// `StaffIngressDialogHost` mounts once in the app shell and registers as
// the confirmer `fpFetch` asks when the server refuses a staff write with
// 428 `staff_ingress_flagged`. The modal names the client and lists the
// items that were flagged as potentially unintended, and says nothing
// about why: the server sends no reason, so there is none to show. "Add
// anyway" records a thirty-minute grace on the server for this staff
// member in this org, then answers yes so the action is retried; "Cancel"
// answers no and the action fails.
import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "@mui/material";
import { X } from "@phosphor-icons/react";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import {
  recordStaffIngressGrace,
  setStaffIngressConfirmer,
  StaffIngressRefusal,
} from "@/services/staffIngress";

const GRACE_URL = `${BASE_API_URL_V2}/api/v2/staff/ingress-grace`;

const MAX_ITEMS_SHOWN = 8;

const KIND_LABELS: Record<string, [string, string]> = {
  document: ["document", "documents"],
  agent_file: ["agent file", "agent files"],
  skill_file: ["skill file", "skill files"],
  import: ["import", "imports"],
};

/** The items to list: what was flagged, or everything when the server
 * named nothing in particular. */
function flaggedItems(refusal: StaffIngressRefusal): string[] {
  return refusal.flagged.length > 0 ? refusal.flagged : refusal.items;
}

/** The sentence around the organization's name: "The document about to be
 * added to <org> was flagged ...", "2 of the 5 documents ... were flagged
 * ...". */
function phrase(refusal: StaffIngressRefusal): [string, string] {
  const [one, many] = KIND_LABELS[refusal.kind] ?? ["item", "items"];
  const total = refusal.items.length;
  const count = flaggedItems(refusal).length;
  const tail = "flagged as potentially unintended for this client:";
  if (count >= total) {
    return total === 1
      ? [`The ${one} about to be added to `, ` was ${tail}`]
      : [`The ${total} ${many} about to be added to `, ` were ${tail}`];
  }
  return [
    `${count} of the ${total} ${many} about to be added to `,
    ` were ${tail}`,
  ];
}

/** The reason recorded with the grace: what was overridden. */
function overrideReason(refusal: StaffIngressRefusal): string {
  return `flagged: ${flaggedItems(refusal).join(", ")}`;
}

interface StaffIngressDialogProps {
  refusal: StaffIngressRefusal;
  /** Resolves once the answer is final: true after the grace is recorded. */
  onAnswer: (proceeded: boolean) => void;
}

export const StaffIngressDialog: React.FC<StaffIngressDialogProps> = ({
  refusal,
  onAnswer,
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const name = refusal.organization_name;
  const flagged = flaggedItems(refusal);
  const shown = flagged.slice(0, MAX_ITEMS_SHOWN);
  const hidden = flagged.length - shown.length;
  const [before, after] = phrase(refusal);

  const proceed = async (): Promise<void> => {
    setSubmitting(true);
    setFailed(false);
    const response = await fpFetch(GRACE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organization_id: refusal.organization_id,
        reason: overrideReason(refusal),
      }),
    });
    if (!response?.ok) {
      setSubmitting(false);
      setFailed(true);
      return;
    }
    const granted = (await response.json()) as { expires_at: string };
    recordStaffIngressGrace(refusal.organization_id, granted.expires_at);
    onAnswer(true);
  };

  return (
    <Dialog
      open
      // Escape or a backdrop click while the grace is being recorded
      // would answer no to an action the server is about to allow.
      onClose={() => {
        if (!submitting) onAnswer(false);
      }}
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          width: "440px",
          maxWidth: "90vw",
        },
      }}
    >
      <div className="p-4 md:p-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-900">
            Flagged as potentially unintended
          </h2>
          {/* Off while the grace is being recorded, like Cancel: a no given
              then would fail an action the server is about to allow. */}
          <button
            type="button"
            onClick={() => onAnswer(false)}
            disabled={submitting}
            aria-label="Close"
            className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          {before}
          <span className="font-semibold text-gray-900">{name}</span>
          {after}
        </p>

        <ul className="mb-4 max-h-40 overflow-auto rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {shown.map((item) => (
            <li key={item} className="truncate py-0.5">
              {item}
            </li>
          ))}
          {hidden > 0 && (
            <li className="py-0.5 text-red-700">and {hidden} more</li>
          )}
        </ul>

        {failed && (
          <p className="text-xs text-red-600 mb-3">Could not record this.</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onAnswer(false)}
            disabled={submitting}
            className="px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void proceed()}
            disabled={submitting}
            className="px-3 py-2 rounded-md text-sm bg-gray-800 text-white disabled:opacity-40"
          >
            Add anyway
          </button>
        </div>
      </div>
    </Dialog>
  );
};

interface Pending {
  refusal: StaffIngressRefusal;
  resolve: (proceeded: boolean) => void;
}

/** Mounted once in the app shell; answers `requestStaffIngressGrace`. */
export const StaffIngressDialogHost: React.FC = () => {
  const [pending, setPending] = useState<Pending | null>(null);
  // The open question, readable from the unmount cleanup: a host that
  // leaves with a modal up answers it no so the waiting request fails
  // instead of hanging forever.
  const open = useRef<Pending | null>(null);

  useEffect(() => {
    setStaffIngressConfirmer(
      (refusal) =>
        new Promise<boolean>((resolve) => {
          const next = { refusal, resolve };
          open.current = next;
          setPending(next);
        })
    );
    return () => {
      open.current?.resolve(false);
      open.current = null;
      setStaffIngressConfirmer(null);
    };
  }, []);

  if (pending === null) return null;
  return (
    <StaffIngressDialog
      refusal={pending.refusal}
      onAnswer={(proceeded) => {
        open.current = null;
        setPending(null);
        pending.resolve(proceeded);
      }}
    />
  );
};

export default StaffIngressDialogHost;
